import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, ApiError } from "../../lib/api-client";
import { PasswordField } from "../../components/PasswordField";
import { AdminLoginForm } from "../admin/AdminLoginPage";
import { TenantLoginForm } from "../tenant/TenantLoginPage";

type AccessProfile = "reader" | "corporate" | "admin";

export function LoginPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestedRedirect = searchParams.get("redirect");
  const redirect = requestedRedirect?.startsWith("/") && !requestedRedirect.startsWith("//") && !requestedRedirect.includes("\\") ? requestedRedirect : "/minha-biblioteca";

  const profile = searchParams.get("perfil");
  const activeTab: AccessProfile = profile === "corporate" || profile === "admin" ? profile : "reader";
  const [readerMode, setReaderMode] = useState<"login" | "register" | "magic-link">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isAlreadyLoggedIn, setIsAlreadyLoggedIn] = useState(false);

  function setActiveTab(nextProfile: AccessProfile) {
    const params = new URLSearchParams(searchParams);
    params.set("perfil", nextProfile);
    setSearchParams(params, { replace: true });
    setError(null);
    setMessage(null);
    setPassword("");
  }

  useEffect(() => {
    const token = localStorage.getItem("datasci_jwt");
    if (token) {
      setIsAlreadyLoggedIn(true);
    }
  }, []);

  async function handleReaderSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setMessage(null);

    try {
      if (readerMode === "login") {
        const res = await apiClient.post<{ ok: boolean; token?: string }>("/consumer/auth/login", { email, password });
        if (res.token) {
          localStorage.setItem("datasci_jwt", res.token);
        }
        setMessage("Login realizado com sucesso! Redirecionando…");
        setTimeout(() => {
          navigate(redirect);
        }, 600);
      } else if (readerMode === "register") {
        const res = await apiClient.post<{ ok: boolean; token?: string }>("/consumer/auth/register", {
          email,
          password,
          turnstileToken: "placeholder-dev",
        });
        if (res.token) {
          localStorage.setItem("datasci_jwt", res.token);
          setMessage("Conta criada com sucesso! Redirecionando…");
          setTimeout(() => {
            navigate(redirect);
          }, 600);
        } else {
          setMessage("Se este e-mail ainda não tinha conta, ela foi criada. Você já pode entrar.");
          setReaderMode("login");
        }
      } else {
        await apiClient.post("/consumer/auth/magic-link/request", { email });
        setMessage("Se este e-mail existir em nossa base, enviamos um link de acesso direto.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não foi possível concluir a autenticação.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogout() {
    try {
      await apiClient.post("/consumer/auth/logout").catch(() => null);
    } finally {
      localStorage.removeItem("datasci_jwt");
      setIsAlreadyLoggedIn(false);
      setMessage("Sessão encerrada com sucesso.");
    }
  }

  return (
    <div className="login-page">
      <div className="login-page__heading">
        <h1>Entrar na Datasci</h1>
        <p className="text-muted" style={{ fontSize: "var(--text-md)", marginTop: "var(--space-2)" }}>
          Um só lugar para leitores, empresas e administração.
        </p>
      </div>

      {activeTab === "reader" && isAlreadyLoggedIn && (
        <div className="card" style={{ marginBottom: "var(--space-5)", background: "var(--color-surface-sunken)" }}>
          <p style={{ margin: 0, fontWeight: 600 }}>Você já está conectado como leitor.</p>
          <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
            <Link to={redirect} className="btn" style={{ fontSize: "var(--text-sm)" }}>
              Continuar para {redirect === "/minha-biblioteca" ? "Minha Biblioteca" : "página anterior"}
            </Link>
            <button className="btn btn-secondary" onClick={handleLogout} style={{ fontSize: "var(--text-sm)" }}>
              Sair da conta
            </button>
          </div>
        </div>
      )}

      {/* Tabs principais de perfil de acesso */}
      <div role="tablist" className="tab-group" aria-label="Perfil de acesso" onKeyDown={(event) => {
        const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        let next = index;
        if (event.key === "ArrowRight") next = (index + 1) % buttons.length;
        else if (event.key === "ArrowLeft") next = (index + buttons.length - 1) % buttons.length;
        else if (event.key === "Home") next = 0;
        else if (event.key === "End") next = buttons.length - 1;
        else return;
        event.preventDefault();
        buttons[next]?.focus();
        buttons[next]?.click();
      }} style={{ marginBottom: "var(--space-5)" }}>
        <button
          role="tab"
          id="profile-reader"
          aria-controls="access-panel"
          tabIndex={activeTab === "reader" ? 0 : -1}
          aria-selected={activeTab === "reader"}
          onClick={() => {
            setActiveTab("reader");
            setError(null);
          }}
        >
          Leitor
        </button>
        <button
          role="tab"
          id="profile-corporate"
          aria-controls="access-panel"
          tabIndex={activeTab === "corporate" ? 0 : -1}
          aria-selected={activeTab === "corporate"}
          onClick={() => {
            setActiveTab("corporate");
            setError(null);
          }}
        >
          Empresas
        </button>
        <button
          role="tab"
          id="profile-admin"
          aria-controls="access-panel"
          tabIndex={activeTab === "admin" ? 0 : -1}
          aria-selected={activeTab === "admin"}
          onClick={() => {
            setActiveTab("admin");
            setError(null);
          }}
        >
          Administração
        </button>
      </div>

      <div id="access-panel" role="tabpanel" aria-labelledby={`profile-${activeTab}`}>
        {activeTab === "reader" && (
          <div className="card">
            <div role="tablist" className="tab-group" aria-label="Modo de autenticação do leitor" style={{ marginBottom: "var(--space-4)" }}>
              <button role="tab" aria-selected={readerMode === "login"} onClick={() => { setReaderMode("login"); setError(null); }}>
                Entrar
              </button>
              <button role="tab" aria-selected={readerMode === "register"} onClick={() => { setReaderMode("register"); setError(null); }}>
                Criar conta
              </button>
              <button role="tab" aria-selected={readerMode === "magic-link"} onClick={() => { setReaderMode("magic-link"); setError(null); }}>
                Link mágico
              </button>
            </div>

            <form onSubmit={handleReaderSubmit}>
              <div className="field">
                <label htmlFor="login-email">E-mail cadastrado</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  required
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {readerMode !== "magic-link" && (
                <PasswordField
                  id="login-password"
                  label={readerMode === "register" ? "Senha (mínimo 10 caracteres)" : "Senha"}
                  required
                  autoComplete={readerMode === "register" ? "new-password" : "current-password"}
                  minLength={readerMode === "register" ? 10 : undefined}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}

              {error && (
                <p role="alert" style={{ color: "var(--color-danger)", marginTop: "var(--space-3)", fontSize: "var(--text-sm)" }}>
                  {error}
                </p>
              )}

              {message && (
                <p role="status" style={{ color: "var(--color-success)", marginTop: "var(--space-3)", fontSize: "var(--text-sm)" }}>
                  {message}
                </p>
              )}

              <button className="btn" type="submit" disabled={submitting} style={{ width: "100%", marginTop: "var(--space-4)" }}>
                {submitting
                  ? "Processando…"
                  : readerMode === "login"
                  ? "Entrar na conta"
                  : readerMode === "register"
                  ? "Criar conta e continuar"
                  : "Enviar link de acesso"}
              </button>
            </form>

            {redirect && redirect !== "/minha-biblioteca" && (
              <p className="text-muted" style={{ fontSize: "var(--text-xs)", textAlign: "center", marginTop: "var(--space-3)" }}>
                Você será redirecionado para: <code>{redirect}</code>
              </p>
            )}
          </div>
        )}

        {activeTab === "corporate" && <TenantLoginForm />}
        {activeTab === "admin" && <AdminLoginForm />}
      </div>
    </div>
  );
}
